import * as React from 'react'
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'

import armorerShopCatalog from '../data/armorerShopCatalog.json'
import CompactAssetIcon from './CompactAssetIcon'

const PLUGIN_KEY = 'armorer'
const SETTINGS_KEY = 'armorerSettings'
const EMPTY_PLUGIN_CONFIG = Object.freeze({})
const KINGDOMS = Object.freeze([
    ['0', 'Current / Great Empire'],
    ['1', 'Great Empire'],
    ['2', 'Everwinter Glacier'],
    ['3', 'Burning Sands'],
    ['4', 'Fire Peaks'],
    ['10', 'Berimond Kingdom']
])

const cloneValue = value => typeof structuredClone === 'function' ?
    structuredClone(value) :
    JSON.parse(JSON.stringify(value ?? {}))

const digits = (value, fallback = '') => {
    const normalized = String(value ?? '').replace(/[^\d]/g, '')
    return normalized || fallback
}

const positiveDigits = (value, fallback = '1') => {
    const parsed = Number.parseInt(digits(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : fallback
}

export const normalizeArmorerSettings = pluginConfig => {
    const source = pluginConfig?.[SETTINGS_KEY] && typeof pluginConfig[SETTINGS_KEY] === 'object' ?
        pluginConfig[SETTINGS_KEY] :
        {}
    const sourcePurchases = source.purchases && typeof source.purchases === 'object' ? source.purchases : {}

    return {
        kingdomID: KINGDOMS.some(([id]) => id === String(source.kingdomID)) ? String(source.kingdomID) : '0',
        intervalMinutes: positiveDigits(source.intervalMinutes, '30'),
        maxPurchasesPerRun: positiveDigits(source.maxPurchasesPerRun, '3'),
        allowRubyPurchases: source.allowRubyPurchases === true,
        purchases: Object.fromEntries(armorerShopCatalog.items.map(item => {
            const purchase = sourcePurchases[String(item.packageID)]
            return [String(item.packageID), {
                enabled: purchase?.enabled === true,
                amount: positiveDigits(purchase?.amount, '1')
            }]
        }))
    }
}

const formatNumber = value => Number(value || 0).toLocaleString('en-US')

const getLevelLabel = item => {
    if (item.maxLevel != null)
        return `Level ${item.minLevel}-${item.maxLevel}`
    return `Level ${item.minLevel}+`
}

const getIconType = item => item.packageType === 'soldier' ? 'troop' :
    item.packageType === 'tool' ? 'tool' :
        item.packageType === 'constructionItem' ? 'building' :
            'resource'

function ArmorerItemIcon({ item }) {
    return (
        <Box
            sx={{
                width: 58,
                height: 58,
                position: 'relative',
                flexShrink: 0,
                borderRadius: '10px',
                border: '1px solid rgba(206, 178, 126, .45)',
                background: 'linear-gradient(180deg, rgba(226,210,181,.96), rgba(182,153,111,.96))'
            }}
        >
            <CompactAssetIcon
                iconUrl={item.iconUrl}
                itemType={getIconType(item)}
                size={58}
            />
            {item.packageType === 'rewardBag' && item.bagMaterialIconUrl ? (
                <CompactAssetIcon
                    iconUrl={item.bagMaterialIconUrl}
                    itemType="resource"
                    size={24}
                    sx={{
                        position: 'absolute',
                        right: -3,
                        bottom: -3,
                        borderRadius: '50%',
                        border: '2px solid #f3ead9',
                        backgroundColor: '#59616d'
                    }}
                />
            ) : null}
        </Box>
    )
}

export default function ArmorerSettingsPanel({ userPlugins, onChange }) {
    const pluginConfig = React.useMemo(() => userPlugins?.[PLUGIN_KEY] ?? EMPTY_PLUGIN_CONFIG, [userPlugins])
    const settings = React.useMemo(() => normalizeArmorerSettings(pluginConfig), [pluginConfig])
    const [filter, setFilter] = React.useState('all')
    const [query, setQuery] = React.useState('')

    const commit = React.useCallback(nextSettings => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = normalizeArmorerSettings({
            [SETTINGS_KEY]: nextSettings
        })
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const updateSettings = React.useCallback(mutator => {
        const nextSettings = cloneValue(settings)
        mutator(nextSettings)
        commit(nextSettings)
    }, [commit, settings])

    const normalizedQuery = query.trim().toLowerCase()
    const visibleItems = armorerShopCatalog.items.filter(item =>
        (filter === 'all' || item.category === filter) &&
        (!normalizedQuery || item.label.toLowerCase().includes(normalizedQuery) || String(item.packageID).includes(normalizedQuery))
    )
    const selectedItems = armorerShopCatalog.items.filter(item => settings.purchases[String(item.packageID)]?.enabled)
    const selectedCosts = selectedItems.reduce((totals, item) => {
        const purchaseCount = Number(settings.purchases[String(item.packageID)]?.amount || 0)
        totals[item.currency] += item.price * purchaseCount
        return totals
    }, { coins: 0, rubies: 0 })

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '22px', bgcolor: 'rgba(18,29,48,.82)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Stack spacing={0.7}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                            <StorefrontRoundedIcon />
                            <Typography variant="h5" fontWeight={800}>Armorer Settings</Typography>
                        </Stack>
                        <Typography color="text.secondary">
                            {armorerShopCatalog.items.length} base packages from game data. Select the packages and the number bought per cycle.
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`${selectedItems.length} selected`} />
                        <Chip color="warning" label={`${formatNumber(selectedCosts.coins)} coins`} />
                        <Chip color="error" label={`${formatNumber(selectedCosts.rubies)} rubies`} />
                    </Stack>
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1.5 }}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Purchase kingdom</InputLabel>
                            <Select
                                label="Purchase kingdom"
                                value={settings.kingdomID}
                                onChange={event => updateSettings(next => { next.kingdomID = event.target.value })}
                            >
                                {KINGDOMS.map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Check interval (minutes)"
                            value={settings.intervalMinutes}
                            onChange={event => updateSettings(next => { next.intervalMinutes = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.intervalMinutes = positiveDigits(next.intervalMinutes, '30') })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Max purchases per run"
                            value={settings.maxPurchasesPerRun}
                            onChange={event => updateSettings(next => { next.maxPurchasesPerRun = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.maxPurchasesPerRun = positiveDigits(next.maxPurchasesPerRun, '3') })}
                        />
                    </Grid>
                </Grid>

                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 2 }}>
                    <Switch
                        checked={settings.allowRubyPurchases}
                        onChange={event => updateSettings(next => { next.allowRubyPurchases = event.target.checked })}
                    />
                    <Box>
                        <Typography fontWeight={800}>Allow ruby purchases</Typography>
                        <Typography variant="body2" color="text.secondary">Off by default; selected ruby packages are skipped until enabled.</Typography>
                    </Box>
                </Stack>
            </Paper>

            <Alert severity="info">
                “Best seller” offers and discounts are server-side and may change. The prices below are the base package prices in the installed game data.
            </Alert>

            <Paper sx={{ p: 2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Stack spacing={1.5}>
                    <TextField
                        fullWidth
                        label="Search item or package ID"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                    />
                    <ToggleButtonGroup
                        exclusive
                        value={filter}
                        onChange={(_, value) => value && setFilter(value)}
                        sx={{ flexWrap: 'wrap' }}
                    >
                        {armorerShopCatalog.filters.map(option => (
                            <ToggleButton key={option.key} value={option.key} sx={{ textTransform: 'none' }}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            </Paper>

            <Grid container spacing={1.5}>
                {visibleItems.map(item => {
                    const key = String(item.packageID)
                    const purchase = settings.purchases[key]
                    const blockedRuby = item.currency === 'rubies' && !settings.allowRubyPurchases
                    return (
                        <Grid item xs={12} md={6} xl={4} key={item.packageID}>
                            <Paper sx={{ p: 1.7, height: '100%', borderRadius: '15px', bgcolor: 'rgba(20,31,52,.68)' }}>
                                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                                    <Checkbox
                                        checked={purchase.enabled}
                                        onChange={event => updateSettings(next => { next.purchases[key].enabled = event.target.checked })}
                                    />
                                    <ArmorerItemIcon item={item} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography fontWeight={800}>{item.label}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Receive {formatNumber(item.amount)} · Package #{item.packageID} · {getLevelLabel(item)}
                                        </Typography>
                                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1 }}>
                                            <Chip
                                                size="small"
                                                color={item.currency === 'rubies' ? 'error' : 'warning'}
                                                label={`${formatNumber(item.price)} ${item.currency}`}
                                            />
                                            {item.amount > 1 && <Chip size="small" label={`${formatNumber(item.price / item.amount)} each`} />}
                                            {blockedRuby && <Chip size="small" variant="outlined" label="Ruby lock" />}
                                        </Stack>
                                    </Box>
                                    <TextField
                                        label="Packs"
                                        value={purchase.amount}
                                        disabled={!purchase.enabled}
                                        onChange={event => updateSettings(next => { next.purchases[key].amount = digits(event.target.value) })}
                                        onBlur={() => updateSettings(next => { next.purchases[key].amount = positiveDigits(next.purchases[key].amount, '1') })}
                                        sx={{ width: 88 }}
                                    />
                                </Stack>
                            </Paper>
                        </Grid>
                    )
                })}
            </Grid>
        </Box>
    )
}
